"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"

export interface JobDescriptionValue {
  text: string | null
  filename: string | null
}

interface JobDescriptionInputProps {
  value: JobDescriptionValue
  onChange: (next: JobDescriptionValue) => void
  disabled?: boolean
}

// JD upload is a smaller affordance than the resume — many users won't
// have a JD handy, so we keep the UI lighter. Paste OR file-paste-as-text.
// (Full PDF parsing for JDs is intentionally out of scope for v1 — the
// resume already drives most of the question grounding.)
export function JobDescriptionInput({ value, onChange, disabled }: JobDescriptionInputProps) {
  const [tab, setTab] = useState<"paste" | "upload">("paste")

  async function handleFile(file: File) {
    const text = await file.text().catch(() => "")
    onChange({ text, filename: file.name })
  }

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden">
      <div className="flex border-b border-border">
        <button
          type="button"
          onClick={() => setTab("paste")}
          disabled={disabled}
          className={cn(
            "flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
            tab === "paste"
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Paste JD
        </button>
        <button
          type="button"
          onClick={() => setTab("upload")}
          disabled={disabled}
          className={cn(
            "flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
            tab === "upload"
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Upload .txt
        </button>
      </div>

      {tab === "paste" ? (
        <textarea
          rows={5}
          placeholder="Paste the job description here (optional)…"
          value={value.text ?? ""}
          onChange={(e) => onChange({ text: e.target.value, filename: value.filename })}
          disabled={disabled}
          className="w-full bg-transparent px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none resize-y min-h-[100px]"
        />
      ) : (
        <div className="p-4">
          <label
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-6 cursor-pointer hover:bg-muted/40 transition-colors",
              disabled && "pointer-events-none opacity-60"
            )}
          >
            <span className="text-sm text-muted-foreground">
              {value.filename ? value.filename : "Click to choose a .txt or .md file"}
            </span>
            <input
              type="file"
              accept=".txt,.md,text/plain"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
              }}
              disabled={disabled}
              className="hidden"
            />
          </label>
        </div>
      )}
    </div>
  )
}
